import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { capitalCase } from '@unimpaired/utils'
import React from 'react'
import { twMerge } from 'tailwind-merge'

export type CheckboxVariant = 'default' | 'circle'

export type CheckboxProps = {
  checked: boolean
  onChange?: (checked: boolean) => void
  /** Optional name — used as the accessible label when `label` is not
   * provided, and rendered as the visible label when `noLabel` is false. */
  name?: string
  label?: string
  noLabel?: boolean
  description?: string
  disabled?: boolean
  required?: boolean
  /** Classes applied to the outer container (label wrapper for the
   * `default` variant, the button itself for the `circle` variant). */
  className?: string
  /** Classes applied to the control element itself — the native
   * `<input>` for `default`, the circular button for `circle`. */
  controlClasses?: string
  /** Classes applied to the text label (ignored when `noLabel`). */
  labelClasses?: string
  variant?: CheckboxVariant
  /** Accessible name when no visible label is rendered. Falls back to
   * `label` / `name` if omitted. */
  ariaLabel?: string
  /** Fires from the click handler in addition to `onChange`. Matches
   * the callback shape used by our Button components. */
  onClick?: (checked: boolean) => void
}

/**
 * Standalone (form-less) checkbox primitive used by `CheckboxField` for
 * form-bound checkboxes and by the Todo component for its optional
 * left-side completion marker.
 *
 * Variants:
 *
 *   - **default**: a standard `<input type='checkbox'>` with an
 *     optional label on the right. Uses the native input so native
 *     keyboard / form behaviors still work.
 *
 *   - **circle**: a round button that visually mirrors the
 *     `faCheckCircle` icon used elsewhere for the "Completed" status.
 *     The check icon is always rendered and toggled with
 *     `text-transparent` + background swap so switching between
 *     true/false doesn't affect layout. `role='checkbox'` + aria on
 *     the button keeps it accessible.
 */
export const Checkbox = ({
  checked,
  onChange,
  onClick,
  name,
  label,
  noLabel,
  description,
  disabled,
  required,
  variant = 'default',
  className,
  controlClasses,
  labelClasses,
  ariaLabel,
}: CheckboxProps) => {
  const resolvedLabel =
    label ?? (name ? capitalCase(name) : undefined)
  const accessibleName = ariaLabel ?? resolvedLabel ?? name

  const commit = (next: boolean) => {
    if (disabled) return
    onChange?.(next)
    onClick?.(next)
  }

  if (variant === 'circle') {
    return (
      <button
        type='button'
        role='checkbox'
        aria-checked={checked}
        aria-label={noLabel ? accessibleName : undefined}
        aria-disabled={disabled}
        aria-required={required}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation()
          commit(!checked)
        }}
        className={twMerge(
          'shrink-0 inline-flex items-center justify-center rounded-full border-2 transition',
          'w-5 h-5',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1',
          checked
            ? 'bg-green-500 border-green-500 text-white'
            : 'bg-white border-slate-300 text-transparent hover:border-slate-500',
          disabled && 'opacity-50 cursor-not-allowed hover:border-slate-300',
          className,
          controlClasses,
        )}>
        <FontAwesomeIcon
          icon={faCheck}
          className='text-[10px] leading-none'
          aria-hidden
        />
        {!noLabel && resolvedLabel && (
          <span className={twMerge('sr-only', labelClasses)}>
            {resolvedLabel}
          </span>
        )}
      </button>
    )
  }

  return (
    <label
      className={twMerge(
        'flex items-start gap-3 cursor-pointer',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}>
      <input
        type='checkbox'
        checked={checked}
        disabled={disabled}
        required={required}
        aria-label={noLabel ? accessibleName : undefined}
        onChange={(event) => commit(event.target.checked)}
        className={twMerge(
          'mt-1 h-4 w-4 rounded border-base-300 text-indigo-600 focus:ring-indigo-500',
          controlClasses,
        )}
      />
      {!noLabel && resolvedLabel && (
        <span className='flex flex-col'>
          <span
            className={twMerge(
              'text-sm font-medium text-base-700',
              labelClasses,
            )}>
            {resolvedLabel}
            {required && <span className='pl-1 text-red-400'>*</span>}
          </span>
          {description && (
            <span className='text-xs text-base-500'>{description}</span>
          )}
        </span>
      )}
    </label>
  )
}
